// ResetForgotPasswordComponent.js
import { useEffect, useRef, useState } from "react";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import {
  useI18N,
  useResetPassword,
  useZoomDetection,
} from "common/hooks";

import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { isPasswordOneNumberSymbolValid, isPasswordValid } from "common/utils";
import { BsCheckCircle } from "react-icons/bs";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import Checkmark from "@/components/common/Checkmark";
import Loader from "@/components/common/Loader";
import { useAtom } from "jotai";
import { emailAtom, resetForgotPasswordCodeAtom } from "@/store/atoms";
import { useCustomToast } from "@/hooks/useCustomToast";
import { StandardButton } from "@/components/common/StandardButton";
import { createPageAnalytics } from "common/analytics";

interface EnterPasswordProps {
  onClose: () => void;
  title: string;
}

const ResetForgotPasswordComponent = ({
  onClose,
  title,
}: EnterPasswordProps) => {
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { showModal } = useAuthModals();

  const showToast = useCustomToast();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errorMessage, setErrorMessage] = useState("");

  const [email] = useAtom(emailAtom);
  const [resetForgotPasswordCode] = useAtom(resetForgotPasswordCodeAtom);

  const { resetPassword, error, isLoading, result } = useResetPassword();

  const authPasswordResetAnalytics = createPageAnalytics("auth_password_reset");

  // const navigate = useNavigate({ from:"/" });

  // Generated tracking plan: page_view_page -> auth_password_reset_viewed
  useEffect(() => {
    authPasswordResetAnalytics.track("page_view_page", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (result) {
      showToast(result.message, "success");

      //close existing modal
      onClose();

      // show login popup modal
      showModal(AuthModalScreen.EnterEmail);
    }
  }, [result, isDark]);

  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error, isDark]);

  const handleClose = () => {
    setNewPassword("");
    setConfirmPassword("");
    onClose();
  };

  const handleSave = () => {
    console.log("handleSave");

    // Generated tracking plan: update_password_action_button -> password_reset_completed
    authPasswordResetAnalytics.track("update_password_action_button", {});

    resetPassword({
      email,
      code: resetForgotPasswordCode,
      password: newPassword,
      confirmPassword,
    });
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div>
      {isLoading && <Loader />}

      <section
        className="flex flex-col bg-bg-brand-secondary-default rounded-xl h-[calc(100vh-200px)] overflow-y-auto"
        aria-labelledby="modal-title"
      >
        <header
          className={`bg-bg-brand-secondary-default py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
        >
          <h1
            className="title-s-semibold text-center text-fg-neutral-strong"
            ref={firstFocusRef}
            tabIndex={-1}
            id="modal-title"
          >
            {title}
          </h1>
        </header>

        <div
          className="flex-1 overflow-y-auto py-3 px-3"
        >
          <div className="w-full md:max-w-[70%] mx-auto mt-5">
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-col gap-4 mb-2">
                <FloatingLabelInput
                  ref={newPasswordRef}
                  label="New Password"
                  required
                  type="Password"
                  value={""}
                  placeholder="New Password"
                  aria-invalid={errorMessage.length > 0 ? "true" : "false"}
                  aria-describedby="newPassword-requirements password-match-status"
                  onChange={handleNewPasswordChange}
                />
              </div>

              <div className="flex flex-col gap-4 mb-4">
                <FloatingLabelInput
                  ref={confirmPasswordRef}
                  label="Confirm Password"
                  required
                  type="Password"
                  value={""}
                  placeholder="Confirm Password"
                  aria-invalid={
                    confirmPassword.length > 0 && confirmPassword !== newPassword
                      ? "true"
                      : "false"
                  }
                  onChange={handleConfirmPasswordChange}
                />
              </div>

              <div id="password-match-status" aria-live="polite" role="status">
                {confirmPassword.length > 0 &&
                  newPassword.length > 0 &&
                  confirmPassword === newPassword && (
                    <div className="flex items-center">
                      {/* {confirmPassword}={newPassword} */}
                      <BsCheckCircle
                        size={14}
                        color={isDark ? "#D3D3D9" : "#151A36"}
                        aria-hidden="true"
                      />
                      <span className="ml-2 label-s-medium">
                        {i18n.get("passwordMatches")}
                      </span>
                    </div>
                  )}
                {confirmPassword.length > 0 &&
                  newPassword.length > 0 &&
                  confirmPassword !== newPassword && (
                    <div className="flex-row items-center">
                      <span className="text-fg-error-subtle label-s-medium">
                        {i18n.get("passwordsDontMatchPleaseTryAgain")}
                      </span>
                    </div>
                  )}
              </div>
              {errorMessage.length > 0 && (
                <span
                  className="text-fg-error-subtle mt-5 label-s-medium"
                  role="alert"
                >
                  {errorMessage}
                </span>
              )}
            </div>

            <div id="newPassword-requirements" className="mb-6 text-fg-neutral-subtle body-l-semibold">
              <span>{i18n.get("yourPwdShouldContain")}</span>

              <div className="flex items-center mb-3 mt-3">
                <Checkmark disabled={newPassword.length < 6} />
                <span className="ml-2 label-s-medium">
                  {i18n.get("passwordAtLeast6Chars")}
                </span>
              </div>

              <div className="flex items-center">
                <Checkmark
                  disabled={!isPasswordOneNumberSymbolValid(newPassword)}
                />
                <span className="ml-2 label-s-medium">
                  {i18n.get("passwordOneSymbolOrNumber")}
                </span>
              </div>
            </div>

            <div className="mb-8 mt-6">
              <div className="w-full max-w-96 mx-auto flex flex-col gap-4">
                <WideButton
                  disabled={
                    isLoading ||
                    newPassword.length < 6 ||
                    confirmPassword.length < 6
                  }
                  onClick={() => {
                    console.log("update clicked", newPassword, confirmPassword);
                    if (newPassword !== confirmPassword) {
                      confirmPasswordRef.current?.focus();
                      setErrorMessage(
                        i18n.get("passwordsDontMatchPleaseTryAgain"),
                      );
                      return;
                    }
                    if (!isPasswordValid(newPassword)) {
                      newPasswordRef.current?.focus();
                      setErrorMessage(i18n.get("passwordShouldContain"));
                      return;
                    }
                    setErrorMessage("");
                    handleSave();
                  }}
                >
                  {i18n.get("updatePassword")}
                </WideButton>
                <div className="flex justify-center">
                  <StandardButton
                    onClick={handleClose}
                    variant="link"
                    className="body-l-semibold underline text-links"
                  >
                    {i18n.get("cancel")}
                  </StandardButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResetForgotPasswordComponent;
